// Activity breadcrumb — records which screen the user is on and which tagged
// ([data-cobrowse]) controls they click, so support can see "where were they, what
// did they press". Screen/control level ONLY — never field values. Best-effort by
// design: every failure is swallowed; instrumentation must never break the app.
//
// Storage: one row per browser tab in the activity_trail table, keyed
// (user_id, session_key). This tab is the only writer of its own row, so each
// flush upserts the whole capped event list — no read-modify-append needed.
import { supabase } from "../supabaseClient";

const CLIENT_CAP = 150;  // rolling in-memory cap
const STORE_CAP = 200;   // rolling cap persisted per session row
const FLUSH_MS = 15000;

let events = [];
let route = "";
let dirty = false;
let flushTimer = null;
let started = false;
let userId = "";

function sessionKey() {
  try {
    let k = sessionStorage.getItem("pm_trail_key");
    if (!k) {
      k = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      sessionStorage.setItem("pm_trail_key", k);
    }
    return k;
  } catch { return "no-session-storage"; }
}

function push(ev) {
  events.push(ev);
  if (events.length > CLIENT_CAP) events = events.slice(-CLIENT_CAP);
  dirty = true;
  if (!flushTimer) flushTimer = setTimeout(() => { flushTimer = null; flush(); }, FLUSH_MS);
}

async function flush() {
  if (!dirty || !userId || !supabase) return;
  dirty = false;
  try {
    await supabase.from("activity_trail").upsert({
      user_id: userId,
      session_key: sessionKey(),
      current_route: route,
      events: events.slice(-STORE_CAP),
      last_seen_at: new Date().toISOString(),
    }, { onConflict: "user_id,session_key" });
  } catch { /* best-effort — drop this batch */ }
}

function onClick(e) {
  try {
    const el = e.target && e.target.closest ? e.target.closest("[data-cobrowse]") : null;
    if (!el) return;
    const id = el.getAttribute("data-cobrowse") || undefined;
    const label = (el.getAttribute("aria-label") || el.textContent || "")
      .trim().replace(/\s+/g, " ").slice(0, 40) || undefined;
    push({ t: Date.now(), type: "click", id, label });
  } catch { /* never interfere with the app's own click handling */ }
}

function onHide() {
  try { if (document.visibilityState === "hidden") flush(); } catch { /* ignore */ }
}

/** Record a screen change (call on every view/route change). */
export function trailNav(path) {
  try {
    if (!path || path === route) return;
    route = path;
    push({ t: Date.now(), type: "nav", path });
  } catch { /* ignore */ }
}

/** Start capturing (idempotent). Call once, after sign-in, with the auth user id. */
export function startTrail(uid) {
  try {
    if (started || !uid) return;
    started = true;
    userId = uid;
    document.addEventListener("click", onClick, true); // capture phase — survives stopPropagation
    document.addEventListener("visibilitychange", onHide);
  } catch { /* ignore */ }
}

// ---- pure helpers (unit-tested) ----
export function capTrail(list, n = STORE_CAP) {
  const l = Array.isArray(list) ? list : [];
  return l.length > n ? l.slice(-n) : l;
}

/** One human line, oldest → newest: page clients → click « New lead » → page retail */
export function trailSummary(evts, max = 12) {
  const l = capTrail(evts).slice(-max);
  return l.map((e) => (e.type === "nav" ? `page ${e.path}` : `click « ${e.label || e.id || "?"} »`)).join(" → ");
}
